import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import {
  AgGridEvent,
  ColDef,
  ColumnApi,
  GridApi,
  GridReadyEvent,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ModuleRegistry,
  GridColumnsChangedEvent,
} from '@ag-grid-community/core';
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model';
import { AgGridReact, AgGridReactProps } from '@ag-grid-community/react';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { AG_GRID_LICENCE } from '../../appConfig';
import { EXCEL_STYLES } from './AGTable.constants';
import { defaultColumnDefinition, getColumnState, persistColumnState } from './AGTable.logic';

import './AGTable.scss';
import useWindowResizeListener from '../../hooks/useWindowResizeListener';

export interface AGTableProps<TData> extends AgGridReactProps<TData> {
  additionalDefaultColDef?: ColDef<TData>;
  columnPersistenceKey?: string;
}

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ExcelExportModule,
  RowGroupingModule,
  InfiniteRowModelModule,
]);
LicenseManager.setLicenseKey(AG_GRID_LICENCE);

const AGTable = <TData,>({
  columnPersistenceKey,
  additionalDefaultColDef,
  onColumnResized,
  onColumnPinned,
  onColumnMoved,
  onGridColumnsChanged,
  onGridReady,
  suppressRowHoverHighlight = true,
  ...props
}: AGTableProps<TData>) => {
  const columnApiRef = useRef<ColumnApi | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);

  const gridOptions = useMemo(
    () => ({
      tooltipShowDelay: 1000,
    }),
    []
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      columnApiRef.current = event.columnApi;
      gridApiRef.current = event.api;
      if (!applyColumnState()) {
        resizeColumnsToFit();
      }
      onGridReady?.(event);
    },
    [onGridReady]
  );

  useWindowResizeListener(() => gridApiRef.current?.sizeColumnsToFit());

  const defaultColDef = useMemo(
    () => ({
      ...defaultColumnDefinition,
      ...additionalDefaultColDef,
    }),
    [additionalDefaultColDef]
  );

  const saveColumnState = useCallback(
    (event: AgGridEvent) => {
      if (columnPersistenceKey) {
        persistColumnState(columnPersistenceKey, event.columnApi.getColumnState());
      }
    },
    [columnPersistenceKey]
  );

  const applyColumnState = (): boolean => {
    if (!columnPersistenceKey) {
      return false;
    }
    const columnStateInLocalStorage = getColumnState(columnPersistenceKey);
    if (!columnStateInLocalStorage) {
      return false;
    }
    columnApiRef.current?.applyColumnState({
      state: columnStateInLocalStorage,
      applyOrder: true,
      defaultState: { pinned: null, sort: null, sortIndex: null },
    });
    return true;
  };

  const handleColumnMoved = useCallback((event: ColumnMovedEvent) => {
    onColumnMoved?.(event);
    saveColumnState(event);
  }, []);

  const handleColumnPinned = useCallback((event: ColumnPinnedEvent) => {
    onColumnPinned?.(event);
    saveColumnState(event);
  }, []);

  const handleColumnResized = useCallback((event: ColumnResizedEvent) => {
    onColumnResized?.(event);
    saveColumnState(event);
  }, []);

  const handleGridColumnsChanged = useCallback((event: GridColumnsChangedEvent) => {
    onGridColumnsChanged?.(event);
    saveColumnState(event);
    resizeColumnsToFit();
  }, []);

  const resizeColumnsToFit = useCallback(() => {
    gridApiRef.current?.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    const storageEventHandler = (event: StorageEvent) => {
      if (event.key === columnPersistenceKey) {
        applyColumnState();
      }
    };
    window.addEventListener('storage', storageEventHandler);
    return () => {
      window.removeEventListener('storage', storageEventHandler);
    };
  }, [columnPersistenceKey]);

  return (
    <div className="ag-theme-alpine" style={{ height: '100%' }} data-testid="table">
      <AgGridReact
        gridOptions={gridOptions}
        headerHeight={40}
        rowHeight={33}
        onGridReady={handleGridReady}
        suppressRowHoverHighlight={suppressRowHoverHighlight}
        suppressCellFocus
        defaultColDef={defaultColDef}
        suppressMultiSort
        suppressDragLeaveHidesColumns
        suppressContextMenu
        enableCellTextSelection
        ensureDomOrder
        className="ag-table"
        unSortIcon
        maintainColumnOrder
        onColumnMoved={handleColumnMoved}
        onColumnPinned={handleColumnPinned}
        onColumnResized={handleColumnResized}
        onGridColumnsChanged={handleGridColumnsChanged}
        excelStyles={EXCEL_STYLES}
        {...props}
      />
    </div>
  );
};

export default memo(AGTable) as typeof AGTable;
